<template>
	<div class="canvas-page">
		<div class="canvas-header">
			<button class="back-btn" @click="handleBack">
				<i class="el-icon-arrow-left"></i>
				<span>返回列表</span>
			</button>
			<div class="canvas-title">
				<span class="title-prefix">无限画布</span>
				<span class="title-separator">/</span>
				<el-input v-model="canvasName" class="canvas-name-input" placeholder="点击编辑"></el-input>
				<button class="save-btn" @click="handleSave" style="display: none;">
					<i class="el-icon-save"></i>
					<span>保存</span>
				</button>
			</div>
		</div>
		<div class="canvas-body">
			<div class="left-panel">
				<div class="panel-header">
					<h3>基础节点</h3>
				</div>
				<div class="node-list">
					<div class="node-item" @click="addNode('text')">
						<i class="el-icon-document"></i>
						<span>文本节点</span>
					</div>
					<div class="node-item" @click="addNode('image')">
						<i class="el-icon-picture"></i>
						<span>图片节点</span>
					</div>
					<div class="node-item" @click="addNode('audio')">
						<i class="el-icon-headset"></i>
						<span>音频节点</span>
					</div>
					<div class="node-item" @click="addNode('video')">
						<i class="el-icon-video-camera"></i>
						<span>视频节点</span>
					</div>
				</div>
			</div>
			<div class="canvas-area" ref="canvasContainer" :style="canvasAreaStyle" @mousedown="handleCanvasMouseDown"
				@mousemove="handleCanvasMouseMove" @mouseup="handleCanvasMouseUp" @mouseleave="handleCanvasMouseUp"
				@wheel.prevent="handleWheel">
				<div class="canvas-inner" :style="canvasStyle">
					<svg class="connections-layer" :width="canvasWidth" :height="canvasHeight">
						<path v-for="conn in connections" :key="`${conn.from}-${conn.to}`" :d="getConnectionPath(conn)"
							class="connection-line" fill="none" stroke="#9ca3af" stroke-width="2" />
						<g v-for="conn in connections" :key="`delete-${conn.from}-${conn.to}`">
							<circle :cx="getConnectionMidX(conn)" :cy="getConnectionMidY(conn)" r="14"
								class="connection-delete-btn" @click.stop="removeConnection(conn)" />
							<text :x="getConnectionMidX(conn)" :y="getConnectionMidY(conn)" class="delete-icon"
								text-anchor="middle" dominant-baseline="middle">✂</text>
						</g>
						<path v-if="isConnecting && connectingLine" :d="connectingLine"
							class="connection-line connecting" fill="none" stroke="#10b981" stroke-width="2" />
					</svg>
					<div v-for="node in canvasNodes" :key="node.id" class="canvas-node"
						:class="[node.type, { selected: selectedNodeId === node.id }]" :style="getNodeStyle(node)"
						@mousedown.stop="handleNodeMouseDown($event, node)" @click.stop="handleNodeClick(node)">
						<div class="connection-handle left-handle"
							@mousedown.stop="handleConnectStart($event, node, 'left')"
							@click.stop="showAddMenu = { nodeId: node.id, direction: 'left', x: node.x, y: node.y + node.height / 2 }">
							<i class="el-icon-plus"></i>
						</div>
						<div v-if="selectedNodeId === node.id && (node.type === 'image' || node.type === 'audio' || node.type === 'video')"
							class="node-upload-bar" @click.stop>
							<span class="upload-label">
								<i :class="getNodeIconClass(node.type)"></i>
								{{ node.name }}
							</span>
							<el-dropdown trigger="click" @command="handleUploadCommand($event, node)">
								<span class="upload-btn">
									<i class="el-icon-plus"></i>
									{{ node.type === 'image' ? '选择图片' : (node.type === 'audio' ? '选择音频' : '选择视频') }}
									<i class="el-icon-arrow-down"></i>
								</span>
								<el-dropdown-menu slot="dropdown">
									<el-dropdown-item command="upload">上传本地文件</el-dropdown-item>
									<el-dropdown-item command="library">选择素材库</el-dropdown-item>
								</el-dropdown-menu>
							</el-dropdown>
						</div>
						<div class="node-header">
							<div class="node-title-wrapper">
								<span v-if="!node.editingName" class="node-title" @dblclick="startEditNodeName(node)">
									{{ getNodeTitle(node) }}
								</span>
								<input v-else v-model="node.name" class="node-title-input" :data-node-id="node.id"
									@blur="stopEditNodeName(node)" @keyup.enter="stopEditNodeName(node)" @click.stop>
							</div>
							<i class="el-icon-close" @click.stop="removeNode(node.id)"></i>
						</div>
						<div class="node-content">
							<div v-if="node.type === 'text'" class="text-preview">
								<div v-if="node.isGenerating" class="video-generating">
									<i class="el-icon-loading"></i>
									<span>思考中...</span>
								</div>
								<textarea v-else v-model="node.content" class="text-content"
									placeholder="点击节点后编辑正文..."></textarea>
							</div>
							<div v-else-if="node.type === 'image'" class="image-preview">
								<div v-if="node.isGenerating" class="video-generating">
									<i class="el-icon-loading"></i>
									<span>思考中...</span>
								</div>
								<div v-else-if="node.fileUrl" class="image-wrapper">
									<img :src="node.fileUrl" class="preview-image"
										:alt="node.fileName"
										@click.stop="openImagePreview(node.fileUrl)">
									<div class="image-click-hint">
										<i class="el-icon-zoom-in"></i>
										<span>点击放大</span>
									</div>
								</div>
								<div v-else class="preview-placeholder">
									<i class="el-icon-picture-outline"></i>
									<span>图片</span>
								</div>
							</div>
							<div v-else-if="node.type === 'audio'" class="audio-preview">
								<div v-if="node.fileUrl" class="audio-info">
									<i class="el-icon-headset"></i>
									<span class="audio-name">{{ node.fileName }}</span>
									<audio :src="node.fileUrl" controls class="audio-player"></audio>
								</div>
								<div v-else class="preview-placeholder">
									<i class="el-icon-headset"></i>
									<span>音频</span>
								</div>
							</div>
							<div v-else-if="node.type === 'video'" class="video-preview">
								<div v-if="node.isGenerating" class="video-generating">
									<i class="el-icon-loading"></i>
									<span>请等待2-5分钟...</span>
								</div>
								<div v-else-if="node.fileUrl" class="video-info">
									<video :src="node.fileUrl" controls playsinline webkit-playsinline
										preload="metadata" class="video-player" @click.stop></video>
									<span class="video-name">{{ node.fileName }}</span>
								</div>
								<div v-else class="preview-placeholder">
									<i class="el-icon-video-camera"></i>
									<span>视频</span>
								</div>
							</div>
						</div>
						<div class="connection-handle right-handle"
							@mousedown.stop="handleConnectStart($event, node, 'right')"
							@click.stop="showAddMenu = { nodeId: node.id, direction: 'right', x: node.x + node.width, y: node.y + node.height / 2 }">
							<i class="el-icon-plus"></i>
						</div>
						<div v-if="selectedNodeId === node.id && node.type !== 'audio'" class="text-editor-panel"
							:class="{ expanded: node.panelExpanded }" @mousedown.stop @click.stop>
							<div class="panel-arrow"></div>
							<div class="panel-toolbar">
								<i class="el-icon-full-screen" @click.stop="handleExpandPanel(node)"></i>
								<i class="el-icon-copy-document" @click.stop="handleCopyNode(node)"></i>
								<i class="el-icon-delete" @click.stop="removeNode(node.id)"></i>
							</div>
							<div class="panel-input-wrapper">
								<textarea class="panel-input" v-model="node.prompt" placeholder="输入 prompt，可用 @ 引用节点"
									@input="handlePromptInput($event, node)" @focus="handlePromptFocus($event, node)"
									@blur="handlePromptBlur" @keydown.enter="handlePromptEnter($event)"></textarea>
								<div v-if="showNodeSuggestions" class="node-suggestions" @click.stop>
									<div v-for="suggestNode in suggestedNodes" :key="suggestNode.id"
										class="suggestion-item" @click="handleInsertNodeRef(suggestNode, node)">
										<span class="tag-icon">{{ getNodeTagIcon(suggestNode.type) }}</span>
										<span class="tag-name">{{ suggestNode.name }}</span>
										<i class="el-icon-close tag-close"></i>
									</div>
								</div>
							</div>
							<div class="panel-footer">
								<div class="panel-footer-row">
									<el-select v-model="node.model" class="model-select" size="small"
										v-if="node.type === 'image'">
										<el-option label="Seedream 4.0" value="doubao-seedream-4-0-250828"></el-option>
										<el-option label="Seedream 4.5" value="doubao-seedream-4-5-251128"></el-option>
										<el-option label="Seedream 5.0 lite"
											value="doubao-seedream-5-0-260128"></el-option>
										<el-option label="Seedream 5.0 pro"
											value="doubao-seedream-5-0-pro-260628"></el-option>
									</el-select>
									<el-select v-model="node.model" class="model-select" size="small"
										v-if="node.type === 'video'">
										<el-option label="Seedance 2.0" value="doubao-seedance-2-0-260128"></el-option>
										<el-option label="Seedance 2.0 Fast"
											value="doubao-seedance-2-0-fast-260128"></el-option>
										<el-option label="Seedance 2.0 Mini"
											value="doubao-seedance-2-0-mini-260615"></el-option>
									</el-select>
									<el-select v-model="node.model" class="model-select" size="small"
										v-if="node.type === 'text'">
										<el-option label="doubao-seed-2.0-mini"
											value="doubao-seed-2-0-mini-260428"></el-option>
										<el-option label="doubao-seed-2.0-lite"
											value="doubao-seed-2-0-lite-260428"></el-option>
										<el-option label="doubao-seed-2.0-pro"
											value="doubao-seed-2-0-pro-260215"></el-option>
									</el-select>
									<div v-if="node.type === 'image'" class="image-settings">
										<div class="settings-summary"
											@click="node.settingsExpanded = !node.settingsExpanded">
											<span
												class="summary-text">{{ node.aspectRatio }}·{{ node.resolution }}</span>
											<i
												:class="node.settingsExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
										</div>
										<div class="settings-detail" v-if="node.settingsExpanded">
											<div class="setting-group">
												<span class="setting-label">图片比例</span>
												<div class="aspect-grid">
													<button
														v-for="ratio in ['1:1', '3:2', '2:3', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9']"
														:key="ratio" :class="{ active: node.aspectRatio === ratio }"
														@click="node.aspectRatio = ratio" class="aspect-btn">
														{{ ratio }}
													</button>
												</div>
											</div>
											<div class="setting-group">
												<span class="setting-label">分辨率</span>
												<div class="resolution-group">
													<button v-for="res in ['1K', '2K', '4K']" :key="res"
														:class="{ active: node.resolution === res }"
														@click="node.resolution = res" class="resolution-btn">
														{{ res }}
													</button>
												</div>
											</div>
										</div>
									</div>
									<div v-if="node.type === 'video'" class="video-settings">
										<div class="settings-summary"
											@click="node.settingsExpanded = !node.settingsExpanded">
											<span
												class="summary-text">{{ node.aspectRatio }}·{{ node.resolution }}·{{ node.duration }}s</span>
											<i
												:class="node.settingsExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
										</div>
										<div class="settings-detail" v-if="node.settingsExpanded">
											<div class="setting-group">
												<span class="setting-label">视频比例</span>
												<div class="aspect-grid video">
													<button v-for="ratio in ['16:9', '4:3', '1:1', '3:4', '9:16']"
														:key="ratio" :class="{ active: node.aspectRatio === ratio }"
														@click="node.aspectRatio = ratio" class="aspect-btn">
														{{ ratio }}
													</button>
												</div>
											</div>
											<div class="setting-group">
												<span class="setting-label">分辨率</span>
												<div class="resolution-group">
													<button v-for="res in ['480p', '720p', '1080p']" :key="res"
														:class="{ active: node.resolution === res }"
														@click="node.resolution = res" class="resolution-btn">
														{{ res }}
													</button>
												</div>
											</div>
											<div class="setting-group">
												<span class="setting-label">视频时长</span>
												<div class="duration-group">
													<input type="range" v-model="node.duration" min="5" max="15"
														step="1" class="duration-slider">
													<span class="duration-value">{{ node.duration }}s</span>
												</div>
											</div>
										</div>
									</div>

									<button class="send-btn" @click.stop="handleGenerate(node)">
										<i class="el-icon-upload"></i>
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div v-for="node in canvasNodes" :key="'input_' + node.id" class="hidden-input-wrapper">
					<input type="file" :ref="'uploadInput_' + node.id" class="hidden-input"
						:accept="node.type === 'image' ? 'image/*' : (node.type === 'video' ? 'video/*' : 'audio/*')"
						@change="handleNodeFileUpload($event, node)">
				</div>
				<div v-if="showAddMenu" class="add-menu-overlay" @click="showAddMenu = null">
					<div class="add-menu" :style="getAddMenuStyle()" @click.stop>
						<div class="menu-item" @click="handleAddNodeType('text')">
							<i class="el-icon-document"></i>
							<span>文本节点</span>
						</div>
						<div class="menu-item" @click="handleAddNodeType('image')">
							<i class="el-icon-picture"></i>
							<span>图片节点</span>
						</div>
						<div class="menu-item" @click="handleAddNodeType('audio')">
							<i class="el-icon-headset"></i>
							<span>音频节点</span>
						</div>
						<div class="menu-item" @click="handleAddNodeType('video')">
							<i class="el-icon-video-camera"></i>
							<span>视频节点</span>
						</div>
					</div>
				</div>
			</div>
			<div class="toolbar">
				<button class="toolbar-btn" @click="resetCanvas">
					<i class="el-icon-refresh"></i>
					<span>重置视图</span>
				</button>
				<button class="toolbar-btn" @click="organizeCanvas">
					<i class="el-icon-sort"></i>
					<span>整理画布</span>
				</button>
				<span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
			</div>
		</div>
		<el-dialog title="选择素材" :visible.sync="showAssetLibrary" width="800px" @open="onAssetLibraryOpen">
			<div class="asset-filter-group">
				<template v-if="currentNode && currentNode.type === 'audio'">
					<button class="filter-btn active" @click="searchForm.type = '2'; fetchAssetLibrary()">音频</button>
				</template>
				<template v-else-if="currentNode && currentNode.type === 'image'">
					<button class="filter-btn" :class="{ active: searchForm.type === '1' }"
						@click="searchForm.type = '1'; fetchAssetLibrary()">图片</button>
					<button class="filter-btn" :class="{ active: searchForm.type === '3' }"
						@click="searchForm.type = '3'; fetchAssetLibrary()">场景</button>
				</template>
				<template v-else-if="currentNode && currentNode.type === 'video'">
					<button class="filter-btn active" @click="searchForm.type = '4'; fetchAssetLibrary()">视频</button>
				</template>
				<template v-else>
					<button class="filter-btn" :class="{ active: !searchForm.type }"
						@click="searchForm.type = ''; fetchAssetLibrary()">全部</button>
					<button class="filter-btn" :class="{ active: searchForm.type === '1' }"
						@click="searchForm.type = '1'; fetchAssetLibrary()">图片</button>
					<button class="filter-btn" :class="{ active: searchForm.type === '2' }"
						@click="searchForm.type = '2'; fetchAssetLibrary()">音频</button>
					<button class="filter-btn" :class="{ active: searchForm.type === '3' }"
						@click="searchForm.type = '3'; fetchAssetLibrary()">场景</button>
					<button class="filter-btn" :class="{ active: searchForm.type === '4' }"
						@click="searchForm.type = '4'; fetchAssetLibrary()">视频</button>
				</template>
			</div>
			<div class="asset-library-list">
				<template v-if="assetLibraryData.length > 0">
					<div v-for="asset in assetLibraryData" :key="asset.resourceId" class="asset-item"
						@click="selectAssetFromLibrary(asset)">
						<div v-if="asset.resourceCate === 1 || asset.resourceCate === 3" class="asset-preview">
							<img :src="asset.resourceUrl" :alt="asset.resourceName">
						</div>
						<div v-else-if="asset.resourceCate === 4" class="asset-preview video-preview-item">
							<video :src="asset.resourceUrl" controls></video>
						</div>
						<div v-else class="asset-preview audio-preview-item">
							<i class="el-icon-headset"></i>
							<audio :src="asset.resourceUrl" controls class="asset-audio-player"></audio>
						</div>
						<span class="asset-name">{{ asset.resourceName }}</span>
					</div>
				</template>
				<div v-else class="empty-state">
					<i class="el-icon-picture-outline"></i>
					<span>暂无素材数据</span>
				</div>
			</div>
		</el-dialog>
		<el-dialog title="上传素材" :visible.sync="showUploadDialog" width="500px">
			<el-form :model="uploadForm" label-width="100px">
				<el-form-item label="资产名称">
					<el-input v-model="uploadForm.resourceName" placeholder="请输入资产名称"></el-input>
				</el-form-item>
				<el-form-item label="是否公开">
					<el-switch v-model="uploadForm.resourceType" active-text="公开" inactive-text="私有"></el-switch>
				</el-form-item>
				<el-form-item label="资产类型">
					<el-radio-group v-model="uploadForm.resourceCate">
						<el-radio label="1">形象</el-radio>
						<el-radio label="2">音频</el-radio>
						<el-radio label="3">场景</el-radio>
						<el-radio label="4">视频</el-radio>
					</el-radio-group>
				</el-form-item>
				<el-form-item label="素材性别">
					<el-radio-group v-model="uploadForm.resourceGender">
						<el-radio :label="1">男</el-radio>
						<el-radio :label="0">女</el-radio>
						<el-radio :label="2">其他</el-radio>
					</el-radio-group>
				</el-form-item>
				<el-form-item label="素材上传">
					<el-upload :action="`${baseUrl}/resource/upload?token=${this.adminInfo.adminToken}`" :accept="getUploadAcceptType()"
						:show-file-list="true" :file-list="uploadFileList" :on-success="handleUploadSuccess"
						:on-change="handleUploadChange" :auto-upload="false" :limit="1" :on-exceed="handleUploadExceed">
						<el-button size="small" type="primary">选择文件</el-button>
					</el-upload>
				</el-form-item>
				<el-form-item label="素材备注">
					<el-input v-model="uploadForm.resourceRemark" type="textarea" placeholder="请输入备注信息"
						:rows="3"></el-input>
				</el-form-item>
			</el-form>
			<div slot="footer" class="dialog-footer">
				<el-button @click="showUploadDialog = false">取消</el-button>
				<el-button type="primary" @click="submitUploadAsset">确定上传</el-button>
			</div>
		</el-dialog>
		<div v-if="imagePreviewVisible" class="image-preview-modal" @click="closeImagePreview">
			<div class="modal-content" @click.stop>
				<div class="modal-header">
					<span class="modal-title">图片预览</span>
					<button class="close-btn" @click="closeImagePreview">
						<i class="el-icon-close"></i>
					</button>
				</div>
				<div class="modal-body">
					<div class="image-container">
						<img :src="imagePreviewUrl" class="preview-full-image"
							:style="{ transform: `scale(${previewScale})` }">
					</div>
				</div>
				<div class="modal-footer">
					<div class="zoom-controls">
						<button class="zoom-btn" @click="handlePreviewZoomOut" :disabled="previewScale <= 0.2">
							<i class="el-icon-zoom-out"></i>
						</button>
						<span class="zoom-value">{{ Math.round(previewScale * 100) }}%</span>
						<button class="zoom-btn" @click="handlePreviewZoomIn" :disabled="previewScale >= 5">
							<i class="el-icon-zoom-in"></i>
						</button>
						<button class="zoom-btn" @click="handlePreviewReset">
							<i class="el-icon-refresh"></i>
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import axios from 'axios'

	const BASE_URL = 'https://ai.boningnet.cn/api'
	export default {
		name: 'Canvas',
		data() {
			return {
				canvasName: '',
				canvasNodes: [],
				connections: [],
				nodeIdCounter: 1,
				canvasWidth: 8000,
				canvasHeight: 8000,
				scale: 1,
				offsetX: 0,
				offsetY: 0,
				isDragging: false,
				isNodeDragging: false,
				dragStartX: 0,
				dragStartY: 0,
				dragOffsetX: 0,
				dragOffsetY: 0,
				draggingNode: null,
				showAddMenu: null,
				selectedNodeId: null,
				clickStartX: 0,
				clickStartY: 0,
				isConnecting: false,
				showNodeSuggestions: false,
				suggestedNodes: [],
				connectingFrom: null,
				connectingLine: '',
				connectingEndPoint: null,
				showAssetLibrary: false,
				currentNode: null,
				assetLibraryData: [],
				searchForm: {
					type: ''
				},
				isGenerating: false,
				showUploadDialog: false,
				uploadingNode: null,
				baseUrl: BASE_URL,
				uploadForm: {
					resourceName: '',
					resourceType: true,
					resourceCate: '1',
					resourceGender: 1,
					resourceRemark: ''
				},
				uploadFileList: [],
				uploadedFileUrl: '',
				imagePreviewVisible: false,
				imagePreviewUrl: '',
				previewScale: 1,
				adminInfo: {}
			}
		},
		computed: {
			canvasStyle() {
				return {
					transform: `translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale})`,
					transformOrigin: '0 0'
				}
			},
			canvasAreaStyle() {
				return {
					backgroundImage: `
						linear-gradient(rgba(99, 102, 241, 0.06) 1px, transparent 1px),
						linear-gradient(90deg, rgba(99, 102, 241, 0.06) 1px, transparent 1px)
					`,
					backgroundSize: `${20 * this.scale}px ${20 * this.scale}px`,
					backgroundPosition: `${this.offsetX}px ${this.offsetY}px`
				}
			}
		},
		methods: {
			handleBack() {
				this.$router.push('/assignment')
			},
			handleSave() {
				this.$confirm('是否确定保存画布内容', '提示', {
					confirmButtonText: '确定',
					cancelButtonText: '取消',
					type: 'warning'
				}).then(() => {
					this.saveCanvas()
				}).catch(() => {})
			},
			generateUid() {
				const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
				let uid = ''
				for (let i = 0; i < 30; i++) {
					uid += chars.charAt(Math.floor(Math.random() * chars.length))
				}
				return uid
			},
			async saveCanvas() {
				const adminId = this.adminInfo.adminId
				const existingCanvasId = this.$route.query.canvasId
				let canvasId = 0

				if (!existingCanvasId) {
					const canvasUid = this.generateUid()
					const canvasUName = this.canvasName

					let canvasContent = ''
					if (this.canvasNodes.length > 0) {
						const rightmostNode = this.canvasNodes.reduce((prev, curr) =>
							prev.x + prev.width > curr.x + curr.width ? prev : curr
						)
						canvasContent = rightmostNode.type === 'text' ? rightmostNode.content : (rightmostNode
							.fileUrl || '')
					}

					const canvasData = {
						canvasUid: canvasUid,
						canvasUName: canvasUName,
						adminId: adminId,
						canvasContent: canvasContent
					}
					const res = await axios.post(`${BASE_URL}/canvas/add?token=${this.adminInfo.adminToken}`, canvasData)
					if (res.data && res.data.canvasId > 0) {
						canvasId = res.data.canvasId
					}
				} else {
					canvasId = parseInt(existingCanvasId)
				}

				if (canvasId > 0) {
					this.$message.success('画布保存成功')
					const nodeAssignmentMap = {}

					this.canvasNodes.forEach(node => {
						if (node.assignmentId) {
							nodeAssignmentMap[node.id] = node.assignmentId
						}
					})

					for (const node of this.canvasNodes) {
						const leftConnections = this.connections.filter(conn => conn.to === node.id)
						const rightConnections = this.connections.filter(conn => conn.from === node.id)

						const leftIds = leftConnections
							.map(conn => nodeAssignmentMap[conn.from])
							.filter(id => id)
						const rightIds = rightConnections
							.map(conn => nodeAssignmentMap[conn.to])
							.filter(id => id)

						const assignmentLeftId = leftIds.length > 0 ? leftIds.join(',') : 0
						const assignmentRightId = rightIds.length > 0 ? rightIds.join(',') : 0

						const typeMap = {
							text: 1,
							image: 2,
							audio: 3,
							video: 4
						}
						const saveData = {
							assignmentId: node.assignmentId || '',
							assignmentName: node.name,
							assignmentContent: node.type === 'text' ? node.content : (node.fileUrl || ''),
							assignmentType: typeMap[node.type] || 1,
							assignmentTaskid: node.type === 'text' || node.type === 'audio' ? '' : (node.taskId ||
								''),
							assignmentOutContent: node.type === 'audio' ? '' : (node.outContent || ''),
							assignmentPrompt: node.type === 'audio' ? '' : (node.prompt || ''),
							assignmentMode: node.type === 'audio' ? '' : (node.model || ''),
							assignmentRatio: node.type === 'text' || node.type === 'audio' ? '' : (node
								.aspectRatio || ''),
							assignmentResolution: node.type === 'text' || node.type === 'audio' ? '' : (node
								.resolution || ''),
							assignmentDuration: node.type === 'video' ? parseInt(node.duration) : 0,
							adminId: adminId,
							canvasId: canvasId,
							assignmentLeftId: assignmentLeftId,
							assignmentRightId: assignmentRightId
						}
						try {
							const res = await axios.post(`${BASE_URL}/assignment/add?token=${this.adminInfo.adminToken}`, saveData)
							if (res.data && res.data.assignmentId > 0) {
								nodeAssignmentMap[node.id] = res.data.assignmentId
							}
						} catch (err) {
							console.error('保存节点失败:', err)
						}
					}
				}
			},
			async loadCanvasData() {
				const canvasId = this.$route.query.canvasId
				if (!canvasId) return

				try {
					const res = await axios.get(`${BASE_URL}/assignment/list`, {
						params: {
							token: this.adminInfo.adminToken,
							pageNum: 1,
							pageCount: 100,
							canvasId: canvasId
						}
					})

					const data = res.data || {}
					const assignments = data.list || data || []

					if (assignments.length > 0) {
						this.canvasName = assignments[0].canvasUName || this.canvasName
					}

					if (assignments.length === 0) return

					const assignmentMap = {}
					const nodes = []

					for (let i = 0; i < assignments.length; i++) {
						const item = assignments[i]
						let nodeType = 'text'
						if (item.assignmentType === 2) {
							nodeType = 'image'
						} else if (item.assignmentType === 3) {
							nodeType = 'audio'
						} else if (item.assignmentType === 4) {
							nodeType = 'video'
						} else if (item.assignmentMode && item.assignmentMode.includes('seedance')) {
							nodeType = 'video'
						} else if (item.assignmentMode && item.assignmentMode.includes('seedream')) {
							nodeType = 'image'
						} else if (item.assignmentMode && item.assignmentMode.includes('audio')) {
							nodeType = 'audio'
						}

						const node = {
							id: this.nodeIdCounter++,
							type: nodeType,
							name: item.assignmentName || '',
							x: 100 + (i * 450),
							y: 100,
							width: 400,
							height: nodeType === 'text' ? 240 : 300,
							content: nodeType === 'text' ? (item.assignmentContent || '') : '',
							prompt: item.assignmentPrompt || '',
							model: item.assignmentMode || '',
							aspectRatio: item.assignmentRatio || '',
							resolution: item.assignmentResolution || '',
							duration: item.assignmentDuration || 5,
							settingsExpanded: false,
							isGenerating: false,
							fileUrl: nodeType === 'text' ? (item.assignmentOutContent || '') : (item
								.assignmentContent || ''),
							fileName: '',
							taskId: item.assignmentTaskid || '',
							outContent: item.assignmentOutContent || '',
							assignmentId: item.assignmentId || ''
						}

						assignmentMap[item.assignmentId] = node.id
						nodes.push(node)
					}

					this.canvasNodes = nodes

					for (const item of assignments) {
						if (item.assignmentLeftId) {
							const leftIds = String(item.assignmentLeftId).split(',').map(id => parseInt(id.trim()))
								.filter(id => id > 0)
							for (const leftId of leftIds) {
								if (assignmentMap[leftId]) {
									const exists = this.connections.some(c => c.from === assignmentMap[leftId] && c
										.to === assignmentMap[item.assignmentId])
									if (!exists) {
										this.connections.push({
											from: assignmentMap[leftId],
											to: assignmentMap[item.assignmentId],
											direction: 'right'
										})
									}
								}
							}
						}
						if (item.assignmentRightId) {
							const rightIds = String(item.assignmentRightId).split(',').map(id => parseInt(id.trim()))
								.filter(id => id > 0)
							for (const rightId of rightIds) {
								if (assignmentMap[rightId]) {
									const exists = this.connections.some(c => c.from === assignmentMap[item
										.assignmentId] && c.to === assignmentMap[rightId])
									if (!exists) {
										this.connections.push({
											from: assignmentMap[item.assignmentId],
											to: assignmentMap[rightId],
											direction: 'right'
										})
									}
								}
							}
						}
					}

				} catch (err) {
					console.error('加载画布数据失败:', err)
				}
			},
			generateCanvasName() {
				const now = new Date()
				const year = now.getFullYear()
				const month = String(now.getMonth() + 1).padStart(2, '0')
				const day = String(now.getDate()).padStart(2, '0')
				const hours = String(now.getHours()).padStart(2, '0')
				const minutes = String(now.getMinutes()).padStart(2, '0')
				const seconds = String(now.getSeconds()).padStart(2, '0')
				const ms = String(now.getMilliseconds()).padStart(3, '0')
				return `新画布-${year}${month}${day}${hours}${minutes}${seconds}${ms}`
			},
			addNode(type) {
				const typeNames = {
					text: '文本节点',
					image: '图片节点',
					audio: '音频节点',
					video: '视频节点'
				}
				const count = this.canvasNodes.filter(n => n.type === type).length + 1
				const newNode = {
					id: this.nodeIdCounter++,
					type: type,
					name: `${typeNames[type]}${count}`,
					x: 100 + (this.canvasNodes.length * 100),
					y: 100 + (this.canvasNodes.length * 100),
					width: 400,
					height: type === 'text' ? 240 : 300,
					content: '',
					prompt: '',
					model: type === 'video' ? 'doubao-seedance-2-0-260128' : type === 'text' ?
						'doubao-seed-2-0-mini-260428' : 'doubao-seedream-5-0-pro-260628',
					aspectRatio: type === 'video' ? '9:16' : '3:4',
					resolution: type === 'video' ? '480p' : '1K',
					duration: 5,
					settingsExpanded: false,
					isGenerating: false,
					fileUrl: '',
					fileName: '',
					taskId: '',
					outContent: ''
				}
				this.canvasNodes.push(newNode)
			},
			addConnectedNode(sourceNode, direction, type) {
				const offsetX = direction === 'left' ? -500 : 500
				const nodeHeight = type === 'text' ? 240 : 300
				const typeNames = {
					text: '文本节点',
					image: '图片节点',
					audio: '音频节点',
					video: '视频节点'
				}
				const count = this.canvasNodes.filter(n => n.type === type).length + 1
				const newNode = {
					id: this.nodeIdCounter++,
					type: type,
					name: `${typeNames[type]}${count}`,
					x: sourceNode.x + offsetX,
					y: sourceNode.y,
					width: 400,
					height: nodeHeight,
					content: '',
					prompt: '',
					model: type === 'video' ? 'doubao-seedance-2-0-260128' : type === 'text' ?
						'doubao-seed-2-0-mini-260428' : 'doubao-seedream-5-0-pro-260628',
					aspectRatio: type === 'video' ? '9:16' : '3:4',
					resolution: type === 'video' ? '480p' : '1K',
					duration: 5,
					settingsExpanded: false,
					isGenerating: false,
					fileUrl: '',
					fileName: '',
					taskId: '',
					outContent: ''
				}
				this.canvasNodes.push(newNode)
				this.connections.push({
					from: sourceNode.id,
					to: newNode.id,
					direction: direction
				})
			},
			handleAddNodeType(type) {
				if (!this.showAddMenu) return
				const sourceNode = this.canvasNodes.find(n => n.id === this.showAddMenu.nodeId)
				if (sourceNode) {
					this.addConnectedNode(sourceNode, this.showAddMenu.direction, type)
				}
				this.showAddMenu = null
			},
			getAddMenuStyle() {
				if (!this.showAddMenu) return {}
				const rect = this.$refs.canvasContainer.getBoundingClientRect()
				const x = this.showAddMenu.x * this.scale + this.offsetX + rect.left
				const y = this.showAddMenu.y * this.scale + this.offsetY + rect.top
				return {
					left: `${x}px`,
					top: `${y}px`
				}
			},
			removeNode(id) {
				this.canvasNodes = this.canvasNodes.filter(node => node.id !== id)
				this.connections = this.connections.filter(conn => conn.from !== id && conn.to !== id)
				if (this.showAddMenu && this.showAddMenu.nodeId === id) {
					this.showAddMenu = null
				}
				if (this.selectedNodeId === id) {
					this.selectedNodeId = null
				}
			},
			openImagePreview(url) {
				this.imagePreviewUrl = url
				this.previewScale = 1
				this.imagePreviewVisible = true
			},
			closeImagePreview() {
				this.imagePreviewVisible = false
				this.imagePreviewUrl = ''
				this.previewScale = 1
			},
			handlePreviewZoomIn() {
				this.previewScale = Math.min(5, this.previewScale * 1.2)
			},
			handlePreviewZoomOut() {
				this.previewScale = Math.max(0.2, this.previewScale / 1.2)
			},
			handlePreviewReset() {
				this.previewScale = 1
			},
			getNodeTitle(node) {
				const titles = {
					text: '文本节点',
					image: '图片节点',
					audio: '音频节点',
					video: '视频节点'
				}
				return node.name || titles[node.type] || '节点'
			},
			getNodeTagIcon(type) {
				const icons = {
					text: 'T',
					image: '图',
					audio: '音',
					video: '视'
				}
				return icons[type] || '节'
			},
			isDefaultNodeName(node) {
				const titles = {
					text: '文本节点',
					image: '图片节点',
					audio: '音频节点',
					video: '视频节点'
				}
				return node.name.startsWith(titles[node.type])
			},
			startEditNodeName(node) {
				node.editingName = true
				this.$nextTick(() => {
					const input = document.querySelector(`.node-title-input[data-node-id="${node.id}"]`)
					if (input) {
						input.focus()
						input.select()
					}
				})
			},
			stopEditNodeName(node) {
				node.editingName = false
				if (!node.name.trim()) {
					node.name = this.getNodeTitle({
						type: node.type
					})
				}
			},
			getNodeStyle(node) {
				return {
					left: `${node.x}px`,
					top: `${node.y}px`,
					width: `${node.width}px`,
					height: `${node.height}px`
				}
			},
			getConnectionPath(conn) {
				const fromNode = this.canvasNodes.find(n => n.id === conn.from)
				const toNode = this.canvasNodes.find(n => n.id === conn.to)
				if (!fromNode || !toNode) return ''

				const fromX = fromNode.x + (conn.direction === 'left' ? 0 : fromNode.width)
				const fromY = fromNode.y + fromNode.height / 2
				const toX = toNode.x + (conn.direction === 'left' ? toNode.width : 0)
				const toY = toNode.y + toNode.height / 2

				const midX = (fromX + toX) / 2
				return `M ${fromX} ${fromY} C ${midX} ${fromY}, ${midX} ${toY}, ${toX} ${toY}`
			},
			getConnectionMidX(conn) {
				const fromNode = this.canvasNodes.find(n => n.id === conn.from)
				const toNode = this.canvasNodes.find(n => n.id === conn.to)
				if (!fromNode || !toNode) return 0
				const fromX = fromNode.x + (conn.direction === 'left' ? 0 : fromNode.width)
				const toX = toNode.x + (conn.direction === 'left' ? toNode.width : 0)
				return (fromX + toX) / 2
			},
			getConnectionMidY(conn) {
				const fromNode = this.canvasNodes.find(n => n.id === conn.from)
				const toNode = this.canvasNodes.find(n => n.id === conn.to)
				if (!fromNode || !toNode) return 0
				const fromY = fromNode.y + fromNode.height / 2
				const toY = toNode.y + toNode.height / 2
				return (fromY + toY) / 2
			},
			removeConnection(conn) {
				this.connections = this.connections.filter(c =>
					!(c.from === conn.from && c.to === conn.to)
				)
			},
			organizeCanvas() {
				if (this.canvasNodes.length === 0) return

				const adjacency = {}
				this.canvasNodes.forEach(node => {
					adjacency[node.id] = {
						next: [],
						prev: []
					}
				})

				this.connections.forEach(conn => {
					if (conn.direction === 'right') {
						adjacency[conn.from].next.push(conn.to)
						adjacency[conn.to].prev.push(conn.from)
					} else {
						adjacency[conn.to].next.push(conn.from)
						adjacency[conn.from].prev.push(conn.to)
					}
				})

				const nodeLevels = {}
				this.canvasNodes.forEach(node => {
					nodeLevels[node.id] = 0
				})

				let changed = true
				let iterations = 0
				while (changed && iterations < 100) {
					changed = false
					iterations++
					this.connections.forEach(conn => {
						let fromId, toId
						if (conn.direction === 'right') {
							fromId = conn.from
							toId = conn.to
						} else {
							fromId = conn.to
							toId = conn.from
						}
						const expectedLevel = nodeLevels[fromId] + 1
						if (nodeLevels[toId] < expectedLevel) {
							nodeLevels[toId] = expectedLevel
							changed = true
						}
					})
				}

				const levels = {}
				this.canvasNodes.forEach(node => {
					const level = nodeLevels[node.id]
					if (!levels[level]) {
						levels[level] = []
					}
					levels[level].push(node)
				})

				const levelKeys = Object.keys(levels).map(Number).sort((a, b) => a - b)

				const nodeWidth = 400
				const nodeHeight = 300
				const horizontalGap = 100
				const verticalGap = 50

				const levelPositions = {}
				let maxLevelHeight = nodeHeight

				levelKeys.forEach((levelIndex) => {
					const levelNodes = levels[levelIndex]
					maxLevelHeight = Math.max(maxLevelHeight, levelNodes.length * (nodeHeight + verticalGap) -
						verticalGap)
				})

				const baseY = 200

				levelKeys.forEach((levelIndex, keyIndex) => {
					let levelNodes = levels[levelIndex]

					if (keyIndex > 0) {
						const prevLevel = levels[levelKeys[keyIndex - 1]]
						const prevLevelPositions = levelPositions[levelKeys[keyIndex - 1]] || {}

						levelNodes.sort((a, b) => {
							const aPrevIndices = adjacency[a.id].prev.map(pid => prevLevelPositions[
								pid] !== undefined ? prevLevelPositions[pid] : -1)
							const bPrevIndices = adjacency[b.id].prev.map(pid => prevLevelPositions[
								pid] !== undefined ? prevLevelPositions[pid] : -1)

							const aAvg = aPrevIndices.length > 0 ? aPrevIndices.reduce((sum, idx) => sum +
								idx, 0) / aPrevIndices.length : 0
							const bAvg = bPrevIndices.length > 0 ? bPrevIndices.reduce((sum, idx) => sum +
								idx, 0) / bPrevIndices.length : 0

							return aAvg - bAvg
						})
					}

					const levelPositionsForThisLevel = {}
					const totalHeight = levelNodes.length * (nodeHeight + verticalGap) - verticalGap
					const startY = baseY + (maxLevelHeight - totalHeight) / 2

					levelNodes.forEach((node, nodeIndex) => {
						node.x = 100 + keyIndex * (nodeWidth + horizontalGap)
						node.y = startY + nodeIndex * (nodeHeight + verticalGap)
						levelPositionsForThisLevel[node.id] = nodeIndex
					})

					levelPositions[levelIndex] = levelPositionsForThisLevel
				})

				this.resetCanvas()
			},
			handleCanvasMouseDown(e) {
				const target = e.target
				if (!target.closest('.canvas-node') && !target.closest('.node-menu') && !target.closest(
						'.el-dropdown-menu')) {
					this.isDragging = true
					this.dragStartX = e.clientX
					this.dragStartY = e.clientY
					this.dragOffsetX = this.offsetX
					this.dragOffsetY = this.offsetY
					this.selectedNodeId = null
				}
			},
			handleCanvasMouseMove(e) {
				if (this.isDragging) {
					this.offsetX = this.dragOffsetX + (e.clientX - this.dragStartX)
					this.offsetY = this.dragOffsetY + (e.clientY - this.dragStartY)
				} else if (this.isNodeDragging && this.draggingNode) {
					const rect = this.$refs.canvasContainer.getBoundingClientRect()
					this.draggingNode.x = this.dragOffsetX + (e.clientX - rect.left - this.dragStartX) / this.scale
					this.draggingNode.y = this.dragOffsetY + (e.clientY - rect.top - this.dragStartY) / this.scale
				} else if (this.isConnecting && this.connectingFrom) {
					this.updateConnectingLine(e)
				}
			},
			handleCanvasMouseUp(e) {
				if (this.isConnecting) {
					this.handleConnectEnd(e)
				}
				this.isDragging = false
				this.isNodeDragging = false
				this.draggingNode = null
			},
			handleConnectStart(e, node, direction) {
				this.isConnecting = true
				this.connectingFrom = {
					nodeId: node.id,
					direction: direction
				}
				this.updateConnectingLine(e)
			},
			updateConnectingLine(e) {
				if (!this.connectingFrom) return
				const fromNode = this.canvasNodes.find(n => n.id === this.connectingFrom.nodeId)
				if (!fromNode) return

				const rect = this.$refs.canvasContainer.getBoundingClientRect()
				const mouseX = (e.clientX - rect.left - this.offsetX) / this.scale
				const mouseY = (e.clientY - rect.top - this.offsetY) / this.scale

				const fromX = this.connectingFrom.direction === 'left' ? fromNode.x : fromNode.x + fromNode.width
				const fromY = fromNode.y + fromNode.height / 2

				const midX = (fromX + mouseX) / 2
				this.connectingLine = `M ${fromX} ${fromY} C ${midX} ${fromY}, ${midX} ${mouseY}, ${mouseX} ${mouseY}`
			},
			handleConnectEnd(e) {
				if (!this.connectingFrom) {
					this.isConnecting = false
					this.connectingLine = ''
					return
				}

				const rect = this.$refs.canvasContainer.getBoundingClientRect()
				const mouseX = (e.clientX - rect.left - this.offsetX) / this.scale
				const mouseY = (e.clientY - rect.top - this.offsetY) / this.scale

				let targetNode = null
				let targetDirection = null

				for (const node of this.canvasNodes) {
					if (node.id === this.connectingFrom.nodeId) continue

					const leftHandleX = node.x
					const rightHandleX = node.x + node.width
					const handleY = node.y + node.height / 2
					const hitRadius = 40

					if (Math.abs(mouseX - leftHandleX) < hitRadius && Math.abs(mouseY - handleY) < hitRadius) {
						targetNode = node
						targetDirection = 'left'
						break
					}
					if (Math.abs(mouseX - rightHandleX) < hitRadius && Math.abs(mouseY - handleY) < hitRadius) {
						targetNode = node
						targetDirection = 'right'
						break
					}
				}

				if (targetNode) {
					const existingConnection = this.connections.find(c =>
						(c.from === this.connectingFrom.nodeId && c.to === targetNode.id) ||
						(c.from === targetNode.id && c.to === this.connectingFrom.nodeId)
					)

					if (!existingConnection) {
						let fromNodeId = this.connectingFrom.nodeId
						let toNodeId = targetNode.id
						let direction = 'right'

						if (this.connectingFrom.direction === 'right' && targetDirection === 'right') {
							fromNodeId = targetNode.id
							toNodeId = this.connectingFrom.nodeId
							direction = 'left'
						} else if (this.connectingFrom.direction === 'left' && targetDirection === 'left') {
							fromNodeId = targetNode.id
							toNodeId = this.connectingFrom.nodeId
							direction = 'right'
						} else if (this.connectingFrom.direction === 'left' && targetDirection === 'right') {
							direction = 'left'
						}

						this.connections.push({
							from: fromNodeId,
							to: toNodeId,
							direction: direction
						})
					}
				}

				this.isConnecting = false
				this.connectingFrom = null
				this.connectingLine = ''
			},
			handleNodeMouseDown(e, node) {
				this.isNodeDragging = true
				this.draggingNode = node
				this.clickStartX = e.clientX
				this.clickStartY = e.clientY
				const rect = this.$refs.canvasContainer.getBoundingClientRect()
				this.dragStartX = e.clientX - rect.left
				this.dragStartY = e.clientY - rect.top
				this.dragOffsetX = node.x
				this.dragOffsetY = node.y
			},
			handleNodeClick(node) {
				this.selectedNodeId = this.selectedNodeId === node.id ? null : node.id
			},
			handleExpandPanel(node) {
				this.$set(node, 'panelExpanded', !node.panelExpanded)
			},
			handleCopyNode(node) {
				if (node.prompt) {
					navigator.clipboard.writeText(node.prompt).then(() => {
						this.$message.success('复制成功')
					}).catch(() => {
						this.$message.error('复制失败')
					})
				}
			},
			handlePromptEnter(e) {
				e.preventDefault()
			},
			handlePromptFocus(e, node) {
				const value = e.target.value
				const lastAtIndex = value.lastIndexOf('@')
				if (lastAtIndex !== -1) {
					const searchTerm = value.substring(lastAtIndex + 1)
					const connectedNodes = this.getConnectedNodes(node)
					this.suggestedNodes = connectedNodes.filter(n =>
						`${this.getNodeTitle(n)}${n.id}`.toLowerCase().includes(searchTerm.toLowerCase())
					)
					this.showNodeSuggestions = this.suggestedNodes.length > 0
				}
			},
			handlePromptBlur() {
				setTimeout(() => {
					this.showNodeSuggestions = false
				}, 200)
			},
			handleInsertNodeRef(suggestNode, node) {
				const value = node.prompt || ''
				const lastAtIndex = value.lastIndexOf('@')
				if (lastAtIndex !== -1) {
					node.prompt = value.substring(0, lastAtIndex) +
						`@${suggestNode.name}`
				}
				this.showNodeSuggestions = false
			},
			getNodeIconClass(type) {
				const icons = {
					text: 'el-icon-document',
					image: 'el-icon-picture',
					audio: 'el-icon-headset',
					video: 'el-icon-video-camera'
				}
				return icons[type] || 'el-icon-document'
			},
			handleUploadCommand(command, node) {
				if (command === 'upload') {
					this.uploadingNode = node
					this.showUploadDialog = true
					const fileTypeMap = {
						image: '1',
						audio: '2',
						video: '4'
					}
					this.uploadForm.resourceCate = fileTypeMap[node.type] || '1'
					this.uploadForm.resourceName = ''
					this.uploadForm.resourceType = true
					this.uploadForm.resourceGender = 1
					this.uploadForm.resourceRemark = ''
					this.uploadFileList = []
					this.uploadedFileUrl = ''
				} else if (command === 'library') {
					this.showAssetLibrary = true
					this.currentNode = node
				}
			},
			handleNodeFileUpload(e, node) {
				const file = e.target.files[0]
				if (file) {
					const url = URL.createObjectURL(file)
					node.fileUrl = url
					node.fileName = file.name
				}
				e.target.value = ''
			},
			getUploadAcceptType() {
				switch (this.uploadForm.resourceCate) {
					case '1':
					case '3':
						return 'image/*'
					case '2':
						return 'audio/*'
					case '4':
						return 'video/*'
					default:
						return ''
				}
			},
			handleUploadSuccess(res) {
				this.uploadedFileUrl = res.data.url
			},
			handleUploadChange(file, fileList) {
				if (file.status === 'ready') {
					this.uploadFileList = [file]
					if (!this.uploadForm.resourceName) {
						this.uploadForm.resourceName = file.name.replace(/\.[^/.]+$/, '')
					}
				} else if (file.status === 'removed') {
					this.uploadFileList = fileList
					this.uploadedFileUrl = ''
				}
			},
			handleUploadExceed() {
				this.$message.warning('每次仅允许上传一个素材')
			},
			submitUploadAsset() {
				if (!this.uploadForm.resourceName) {
					this.$message.warning('请输入资产名称')
					return
				}
				if (!this.uploadFileList || !this.uploadFileList.length) {
					this.$message.warning('请选择要上传的文件')
					return
				}
				const file = this.uploadFileList[0].raw || this.uploadFileList[0]
				if (!file || !file.name) {
					this.$message.warning('请选择要上传的文件')
					return
				}
				let adminId = this.adminInfo.adminId
				const formData = new FormData()
				formData.append('file', file)
				axios.post(`${BASE_URL}/resource/upload?token=${this.adminInfo.adminToken}`, formData, {
					headers: {
						'Content-Type': 'multipart/form-data'
					}
				}).then(res => {
					const fileUrl = res.data.data.url
					const addData = {
						resourceName: this.uploadForm.resourceName,
						resourceType: this.uploadForm.resourceType ? 1 : 0,
						resourceCate: parseInt(this.uploadForm.resourceCate),
						resourceUrl: fileUrl,
						resourceSize: file.size,
						resourceGender: this.uploadForm.resourceGender,
						resourceRemark: this.uploadForm.resourceRemark,
						adminId: adminId
					}
					axios.post(`${BASE_URL}/resource/add?token=${this.adminInfo.adminToken}`, addData).then(() => {
						if (this.uploadingNode) {
							this.uploadingNode.fileUrl = fileUrl
							this.uploadingNode.fileName = this.uploadForm.resourceName
						}
						this.showUploadDialog = false
						this.$message.success('上传成功')
					}).catch(() => {
						this.$message.error('添加资产失败')
					})
				}).catch(() => {
					this.$message.error('上传文件失败')
				})
			},
			handleGenerate(targetNode) {
				if (this.isGenerating) return
				this.isGenerating = true
				const prompt = targetNode.prompt || ''
				const referencedNodes = []
				const nodePatterns = this.canvasNodes.map(n => {
					return [
						`${this.getNodeTitle(n)}${n.id}`,
						`${this.getNodeTitle(n)}`,
						`${n.name}${n.id}`,
						`${n.name}`
					].map(pattern => ({
						pattern,
						node: n
					}))
				}).flat()
				nodePatterns.sort((a, b) => b.pattern.length - a.pattern.length)
				const regex = /@\s*([^\s@]+)/g
				let match
				let matches = []
				while ((match = regex.exec(prompt)) !== null) {
					matches.push(match)
				}
				matches.forEach(match => {
					const matchedText = match[1]
					for (const {
							pattern,
							node
						}
						of nodePatterns) {
						if (matchedText.startsWith(pattern)) {
							if (!referencedNodes.find(n => n.id === node.id)) {
								referencedNodes.push(node)
							}
							break
						}
					}
				})
				let requestBody = {}
				let apiUrl = ''
				if (targetNode.type === 'video') {
					let videoPrompt = prompt || ''
					referencedNodes.forEach(node => {
						if (node.type === 'text') {
							const textContent = node.content || node.prompt || ''
							if (textContent) {
								if (videoPrompt) {
									videoPrompt += ' ' + textContent
								} else {
									videoPrompt = textContent
								}
							}
						}
					})
					const videoContentParts = []
					if (videoPrompt) {
						videoContentParts.push({
							type: 'text',
							text: videoPrompt
						})
					}
					referencedNodes.forEach(node => {
						if (node.type === 'image' && node.fileUrl) {
							videoContentParts.push({
								type: 'image_url',
								image_url: {
									url: node.fileUrl
								},
								role: 'reference_image'
							})
						} else if (node.type === 'audio' && node.fileUrl) {
							videoContentParts.push({
								type: 'audio_url',
								audio_url: {
									url: node.fileUrl
								},
								role: 'reference_audio'
							})
						} else if (node.type === 'video' && node.fileUrl) {
							videoContentParts.push({
								type: 'video_url',
								video_url: {
									url: node.fileUrl
								},
								role: 'reference_video'
							})
						}
					})
					requestBody = {
						model: targetNode.model,
						content: videoContentParts,
						ratio: targetNode.aspectRatio,
						resolution: targetNode.resolution,
						duration: parseInt(targetNode.duration)
					}
					apiUrl = '/volcano_ark/contents_generations_tasks'
					this.$set(targetNode, 'isGenerating', true)
				} else if (targetNode.type === 'text') {
					const textContent = []
					referencedNodes.forEach(node => {
						if (node.type === 'image' && node.fileUrl) {
							textContent.push({
								type: 'input_image',
								image_url: node.fileUrl.trim().replace(/`/g, '')
							})
						}
					})
					textContent.push({
						type: 'input_text',
						text: prompt
					})
					requestBody = {
						model: targetNode.model,
						input: [{
							role: 'user',
							content: textContent
						}]
					}
					apiUrl = '/volcano_ark/text_generations_tasks'
					this.$set(targetNode, 'isGenerating', true)
				} else if (targetNode.type === 'image') {
					let imagePrompt = prompt || ''
					let imageUrls = []
					referencedNodes.forEach(node => {
						if (node.type === 'text') {
							const textContent = node.content || node.prompt || ''
							if (textContent) {
								if (imagePrompt) {
									imagePrompt += ' ' + textContent
								} else {
									imagePrompt = textContent
								}
							}
						} else if (node.type === 'image' && node.fileUrl) {
							imageUrls.push(node.fileUrl.trim().replace(/`/g, ''))
						}
					})
					if (targetNode.aspectRatio) {
						imagePrompt += ' 图片比例为' + targetNode.aspectRatio
					}
					requestBody = {
						model: targetNode.model,
						prompt: imagePrompt,
						response_format: 'url',
						size: targetNode.resolution || '1K',
						stream: false,
						watermark: false
					}
					if (imageUrls.length > 0) {
						requestBody.image = imageUrls.length === 1 ? imageUrls[0].trim().replace(/`/g, '') : imageUrls
						if (targetNode.model !== 'doubao-seedream-5-0-pro-260628') {
							requestBody.sequential_image_generation = 'disabled'
						}
					}
					apiUrl = '/volcano_ark/generations_images'
					this.$set(targetNode, 'isGenerating', true)
				}
				axios.post(BASE_URL + apiUrl+'?token='+this.adminInfo.adminToken, requestBody)
					.then(res => {
						if (res.data.code == 0) {
							this.$message && this.$message.success ? this.$message.success('生成任务已创建') : alert(
								'生成任务已创建')
							if (targetNode.type === 'video') {
								var task_id = res.data.data.id
								this.$set(targetNode, 'taskId', task_id)
								this.fetchTaskDetail(task_id, targetNode)
							} else if (targetNode.type === 'image') {
								const resultData = res.data.data.data || []
								if (resultData.length > 0 && resultData[0].url) {
									this.$set(targetNode, 'fileUrl', resultData[0].url.trim().replace(/`/g, ''))
									this.$set(targetNode, 'fileName', '生成图片.png')
									this.$set(targetNode, 'outContent', resultData[0].url.trim().replace(/`/g, ''))
								}
								this.$set(targetNode, 'isGenerating', false)
							} else if (targetNode.type === 'text') {
								const output = res.data.data.output || []
								for (const item of output) {
									if (item.type === 'message' && item.content) {
										for (const contentItem of item.content) {
											if (contentItem.type === 'output_text' && contentItem.text) {
												this.$set(targetNode, 'content', contentItem.text)
												this.$set(targetNode, 'outContent', contentItem.text)
												break
											}
										}
									}
								}
								this.$set(targetNode, 'isGenerating', false)
							}
						} else {
							this.$message && this.$message.success ? this.$message.success(res.data.message) : alert(
								'生成任务创建失败')
							if (targetNode.type === 'video' || targetNode.type === 'image' || targetNode.type ===
								'text') {
								this.$set(targetNode, 'isGenerating', false)
							}
						}
					})
					.catch(err => {
						console.error('生成任务创建失败:', err)
						const msg = (err.response && err.response.data && (err.response.data.message || err.response
							.data.error)) || err.message
						this.$message && this.$message.error ? this.$message.error('生成失败: ' + msg) : alert('生成失败: ' +
							msg)
						if (targetNode.type === 'video' || targetNode.type === 'image' || targetNode.type === 'text') {
							this.$set(targetNode, 'isGenerating', false)
						}
					})
					.finally(() => {
						this.isGenerating = false
					})
			},
			fetchTaskDetail(task_id, targetNode) {
				const checkStatus = () => {
					axios.get(BASE_URL + '/volcano_ark/contents_generations_tasks_get', {
						params: {
							token: this.adminInfo.adminToken,
							task_id: task_id,
						}
					}).then(res => {
						if (res.data.code == 0) {
							if (res.data.data.status == 'succeeded') {
								if (targetNode && targetNode.type === 'video') {
									this.$set(targetNode, 'isGenerating', false)
									const videoUrl = (res.data.data.content && res.data.data.content
										.video_url) || res.data.data.video_url
									this.$set(targetNode, 'fileUrl', videoUrl ? videoUrl.trim().replace(/`/g,
										'') : '')
									this.$set(targetNode, 'fileName', '生成视频.mp4')
									this.$set(targetNode, 'outContent', videoUrl ? videoUrl.trim().replace(
										/`/g,
										'') : '')
								}
								this.$message && this.$message.success ? this.$message.success('生成任务已完成') :
									alert('生成任务已完成')
							} else if (res.data.data.status == 'failed') {
								if (targetNode && targetNode.type === 'video') {
									this.$set(targetNode, 'isGenerating', false)
								}
								this.$message && this.$message.error ? this.$message.error('生成任务失败') : alert(
									'生成任务失败')
							} else {
								setTimeout(checkStatus, 5000)
							}
						} else {
							this.$message && this.$message.error ? this.$message.error(res.data.message) :
								alert('获取任务状态失败')
							if (targetNode && targetNode.type === 'video') {
								this.$set(targetNode, 'isGenerating', false)
							}
						}
					}).catch(err => {
						console.error('获取任务详情失败:', err)
						if (targetNode && targetNode.type === 'video') {
							this.$set(targetNode, 'isGenerating', false)
						}
					})
				}
				checkStatus()
			},
			selectAssetFromLibrary(asset) {
				if (this.currentNode) {
					this.currentNode.fileUrl = asset.resourceUrl
					this.currentNode.fileName = asset.resourceName
					this.currentNode.resourceId = asset.resourceId
				}
				this.showAssetLibrary = false
			},
			onAssetLibraryOpen() {
				if (this.currentNode) {
					if (this.currentNode.type === 'audio') {
						this.searchForm.type = '2'
					} else if (this.currentNode.type === 'image') {
						this.searchForm.type = '1'
					} else if (this.currentNode.type === 'video') {
						this.searchForm.type = '4'
					} else {
						this.searchForm.type = ''
					}
				}
				this.fetchAssetLibrary()
			},
			fetchAssetLibrary() {
				const params = {
					token: this.adminInfo.adminToken,
					pageNum: 1,
					pageCount: 99999,
					resourceCate: parseInt(this.searchForm.type)
				}
				if(this.adminInfo.adminLevel>0){
					params.adminId=this.adminInfo.adminId
				}
				const url = BASE_URL + '/resource/list?' + new URLSearchParams(params).toString()
				fetch(url).then(res => res.json()).then(res => {
					this.assetLibraryData = res || []
				})
			},
			getConnectedNodes(node) {
				const connectedIds = new Set()
				this.connections.forEach(conn => {
					if (conn.to === node.id) {
						connectedIds.add(conn.from)
					}
				})
				return this.canvasNodes.filter(n => connectedIds.has(n.id))
			},
			handlePromptInput(e, node) {
				const value = e.target.value
				const lastAtIndex = value.lastIndexOf('@')
				const connectedNodes = this.getConnectedNodes(node)
				if (lastAtIndex !== -1 && lastAtIndex === value.length - 1) {
					this.showNodeSuggestions = connectedNodes.length > 0
					this.suggestedNodes = connectedNodes
				} else if (lastAtIndex !== -1) {
					const searchTerm = value.substring(lastAtIndex + 1)
					this.suggestedNodes = connectedNodes.filter(n =>
						`${this.getNodeTitle(n)}${n.id}`.toLowerCase().includes(searchTerm.toLowerCase())
					)
					this.showNodeSuggestions = this.suggestedNodes.length > 0
				} else {
					this.showNodeSuggestions = false
				}
			},
			handlePromptFocus(e, node) {
				const value = e.target.value
				const lastAtIndex = value.lastIndexOf('@')
				if (lastAtIndex !== -1) {
					const searchTerm = value.substring(lastAtIndex + 1)
					const connectedNodes = this.getConnectedNodes(node)
					this.suggestedNodes = connectedNodes.filter(n =>
						`${this.getNodeTitle(n)}${n.id}`.toLowerCase().includes(searchTerm.toLowerCase())
					)
					this.showNodeSuggestions = this.suggestedNodes.length > 0
				}
			},
			handlePromptBlur() {
				setTimeout(() => {
					this.showNodeSuggestions = false
				}, 200)
			},
			handleInsertNodeRef(suggestNode, targetNode) {
				const value = targetNode.prompt || ''
				const lastAtIndex = value.lastIndexOf('@')
				if (lastAtIndex !== -1) {
					targetNode.prompt = value.substring(0, lastAtIndex) +
						`@${suggestNode.name}`
				}
				this.showNodeSuggestions = false
			},
			resetCanvas() {
				this.scale = 1
				this.offsetX = 0
				this.offsetY = 0
			},
			handleWheel(e) {
				e.preventDefault()
				const delta = e.deltaY > 0 ? -0.1 : 0.1
				this.scale = Math.min(Math.max(this.scale + delta, 0.3), 3)
			},
			handleKeyDown(e) {
				if (e.key === 'Delete' && this.canvasNodes.length > 0) {
					this.removeNode(this.canvasNodes[this.canvasNodes.length - 1].id)
				}
				if (e.key === 'Escape') {
					this.selectedNodeId = null
				}
			}
		},
		mounted() {
			this.adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
			if (this.$route.query.canvasId) {
				this.canvasName = decodeURIComponent(this.$route.query.canvasName || '')
			} else {
				this.canvasName = this.generateCanvasName()
			}
			window.addEventListener('keydown', this.handleKeyDown)
			this.loadCanvasData()
		},
		beforeDestroy() {
			window.removeEventListener('keydown', this.handleKeyDown)
		}
	}
</script>

<style scoped>
	.canvas-page {
		display: flex;
		flex-direction: column;
		height: 100%;
		width: 100%;
		overflow: hidden;
		background-color: #f0f1ff;
	}

	.canvas-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 12px 20px;
		background-color: #fff;
		border-bottom: 1px solid #e0e0ff;
	}

	.back-btn {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 8px 16px;
		background-color: #10b981;
		color: #fff;
		border: none;
		border-radius: 20px;
		cursor: pointer;
		font-size: 14px;
		font-weight: 500;
		transition: all 0.2s ease;
	}

	.back-btn:hover {
		background-color: #059669;
	}

	.back-btn i {
		font-size: 16px;
	}

	.save-btn {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 8px 16px;
		background-color: #6366f1;
		color: #fff;
		border: none;
		border-radius: 20px;
		cursor: pointer;
		font-size: 14px;
		font-weight: 500;
		transition: all 0.2s ease;
		margin-left: 12px;
	}

	.save-btn:hover {
		background-color: #4f46e5;
	}

	.save-btn i {
		font-size: 16px;
	}

	.canvas-title {
		display: flex;
		align-items: center;
		gap: 4px;
		font-size: 16px;
		font-weight: 500;
		color: #374151;
	}

	.title-prefix {
		color: #818cf8;
	}

	.title-separator {
		color: #e0e0ff;
		margin: 0 4px;
	}

	.canvas-name-input {
		width: 200px;
	}

	.canvas-name-input::v-deep .el-input__inner {
		border: none;
		background-color: transparent;
		font-size: 16px;
		font-weight: 500;
		color: #374151;
		cursor: pointer;
		text-align: left;
		padding: 0;
	}

	.canvas-name-input::v-deep .el-input__inner:hover,
	.canvas-name-input::v-deep .el-input__inner:focus {
		background-color: #f3f4ff;
		border-radius: 4px;
	}

	.canvas-name-input::v-deep .el-input__inner::placeholder {
		color: #9ca3af;
	}

	.canvas-body {
		flex: 1;
		display: flex;
		overflow: hidden;
	}

	.left-panel {
		width: 200px;
		background-color: #fff;
		border-right: 1px solid #e0e0ff;
		display: flex;
		flex-direction: column;
	}

	.panel-header {
		padding: 16px 20px;
		border-bottom: 1px solid #e0e0ff;
	}

	.panel-header h3 {
		margin: 0;
		font-size: 16px;
		font-weight: 600;
		color: #1f2937;
	}

	.node-list {
		flex: 1;
		padding: 12px;
	}

	.node-item {
		display: flex;
		align-items: center;
		padding: 12px 16px;
		margin-bottom: 8px;
		background-color: #f5f5ff;
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.node-item:hover {
		background-color: rgba(99, 102, 241, 0.15);
		transform: translateX(4px);
	}

	.node-item i {
		font-size: 20px;
		color: #6366f1;
		margin-right: 12px;
	}

	.node-item span {
		font-size: 14px;
		color: #374151;
		font-weight: 500;
	}

	.canvas-area {
		flex: 1;
		position: relative;
		overflow: hidden;
		cursor: grab;
	}

	.canvas-area:active {
		cursor: grabbing;
	}

	.canvas-inner {
		position: relative;
		width: 8000px;
		height: 8000px;
		min-width: 100%;
		min-height: 100%;
	}

	.connections-layer {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 1;
	}

	.connection-line {
		stroke-linecap: round;
		stroke-linejoin: round;
		pointer-events: none;
	}

	.connection-delete-btn {
		fill: #fff;
		stroke: #ef4444;
		stroke-width: 2;
		cursor: pointer;
		opacity: 0;
		transition: opacity 0.2s;
	}

	.connections-layer:hover .connection-delete-btn {
		opacity: 1;
	}

	.connection-delete-btn:hover {
		fill: #ef4444;
	}

	.delete-icon {
		font-size: 12px;
		fill: #ef4444;
		pointer-events: none;
	}

	.connection-delete-btn:hover+.delete-icon {
		fill: #fff;
	}

	.connection-line.connecting {
		stroke-dasharray: 5, 5;
		animation: dash 0.5s linear infinite;
	}

	@keyframes dash {
		to {
			stroke-dashoffset: -10;
		}
	}

	.canvas-node {
		position: absolute;
		background-color: #fff;
		border-radius: 8px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
		cursor: move;
		transition: box-shadow 0.2s ease, border-color 0.2s ease;
		z-index: 10;
	}

	.canvas-node:hover {
		box-shadow: 0 4px 16px rgba(99, 102, 241, 0.15);
	}

	.canvas-node.selected.text {
		border: 2px solid rgba(16, 185, 129, 0.5);
		box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
	}

	.connection-handle {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		width: 28px;
		height: 28px;
		border-radius: 50%;
		background-color: #10b981;
		color: #fff;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		z-index: 20;
		opacity: 0;
		transition: all 0.2s ease;
		box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
	}

	.canvas-node:hover .connection-handle {
		opacity: 1;
	}

	.connection-handle:hover {
		background-color: #059669;
		transform: translateY(-50%) scale(1.1);
	}

	.left-handle {
		left: -24px;
	}

	.right-handle {
		right: -24px;
	}

	.connection-handle i {
		font-size: 14px;
		font-weight: bold;
	}

	.add-menu-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 1000;
	}

	.add-menu {
		position: absolute;
		background-color: #fff;
		border-radius: 8px;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
		padding: 4px;
		min-width: 140px;
		transform: translate(-50%, -50%);
	}

	.menu-item {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px;
		cursor: pointer;
		border-radius: 4px;
		transition: background-color 0.2s ease;
	}

	.menu-item:hover {
		background-color: rgba(99, 102, 241, 0.1);
	}

	.menu-item i {
		font-size: 16px;
		color: #6366f1;
	}

	.menu-item span {
		font-size: 13px;
		color: #374151;
	}

	.node-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 8px 12px;
		background-color: #f8f9ff;
		border-bottom: 1px solid #e0e0ff;
	}

	.node-title-wrapper {
		display: flex;
		align-items: center;
		flex: 1;
		min-width: 0;
	}

	.node-title {
		font-size: 12px;
		font-weight: 600;
		color: #374151;
		cursor: pointer;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.node-title-input {
		flex: 1;
		font-size: 12px;
		font-weight: 600;
		color: #374151;
		border: 1px solid #10b981;
		border-radius: 4px;
		padding: 2px 6px;
		outline: none;
		background-color: #fff;
	}

	.node-header .el-icon-close {
		font-size: 14px;
		color: #9ca3af;
		cursor: pointer;
		padding: 4px;
		transition: color 0.2s ease;
	}

	.node-header .el-icon-close:hover {
		color: #ef4444;
	}

	.node-content {
		padding: 12px;
		height: calc(100% - 40px);
		display: flex;
	}

	.text-preview {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-self: stretch;
		margin: 0;
		padding: 0;
	}

	.text-content {
		width: 100%;
		height: 100%;
		border: none;
		resize: none;
		background-color: transparent;
		font-size: 13px;
		line-height: 1.5;
		color: #374151;
		outline: none;
		padding: 0;
		margin: 0;
		box-sizing: border-box;
	}

	.text-content::placeholder {
		color: #9ca3af;
	}

	.image-preview,
	.audio-preview,
	.video-preview {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		color: #9ca3af;
		width: 100%;
		height: 100%;
	}

	.image-preview i,
	.audio-preview i,
	.video-preview i {
		font-size: 32px;
		margin-bottom: 8px;
	}

	.image-preview span,
	.audio-preview span,
	.video-preview span {
		font-size: 12px;
	}

	.video-generating {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		color: #9ca3af;
		gap: 8px;
	}

	.video-generating i {
		font-size: 32px;
		animation: spin 1s linear infinite;
	}

	@keyframes spin {
		from {
			transform: rotate(0deg);
		}

		to {
			transform: rotate(360deg);
		}
	}

	.image-wrapper {
		position: relative;
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		overflow: hidden;
	}

	.preview-image {
		max-width: 100%;
		max-height: 200px;
		object-fit: contain;
		border-radius: 4px;
		cursor: zoom-in;
		transition: transform 0.2s ease;
	}

	.preview-image:hover {
		transform: scale(1.05);
	}

	.image-click-hint {
		position: absolute;
		bottom: 10px;
		right: 10px;
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 4px 10px;
		background: rgba(0, 0, 0, 0.5);
		border-radius: 12px;
		color: #fff;
		font-size: 11px;
		opacity: 0;
		transition: opacity 0.2s ease;
	}

	.image-wrapper:hover .image-click-hint {
		opacity: 1;
	}

	.image-click-hint i {
		font-size: 12px;
	}

	.image-preview-modal {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.8);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 9999;
		animation: fadeIn 0.2s ease;
	}

	@keyframes fadeIn {
		from { opacity: 0; }
		to { opacity: 1; }
	}

	.modal-content {
		background: #fff;
		border-radius: 12px;
		width: 90%;
		max-width: 800px;
		max-height: 90vh;
		display: flex;
		flex-direction: column;
		animation: slideUp 0.3s ease;
	}

	@keyframes slideUp {
		from { transform: translateY(20px); opacity: 0; }
		to { transform: translateY(0); opacity: 1; }
	}

	.modal-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 16px 20px;
		border-bottom: 1px solid #e5e7eb;
	}

	.modal-title {
		font-size: 16px;
		font-weight: 600;
		color: #374151;
	}

	.close-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		border: none;
		background: #f3f4f6;
		border-radius: 6px;
		cursor: pointer;
		transition: background 0.2s ease;
	}

	.close-btn:hover {
		background: #e5e7eb;
	}

	.close-btn i {
		font-size: 16px;
		color: #6b7280;
	}

	.modal-body {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 20px;
		overflow: hidden;
		background: #f9fafb;
	}

	.image-container {
		position: relative;
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: auto;
	}

	.preview-full-image {
		max-width: 100%;
		max-height: 500px;
		object-fit: contain;
		border-radius: 8px;
		transition: transform 0.2s ease;
		transform-origin: center center;
	}

	.modal-footer {
		display: flex;
		justify-content: center;
		padding: 16px 20px;
		border-top: 1px solid #e5e7eb;
	}

	.zoom-controls {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.zoom-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		border: none;
		background: #f3f4f6;
		border-radius: 8px;
		cursor: pointer;
		transition: background 0.2s ease;
	}

	.zoom-btn:hover:not(:disabled) {
		background: #e5e7eb;
	}

	.zoom-btn:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.zoom-btn i {
		font-size: 16px;
		color: #374151;
	}

	.zoom-value {
		font-size: 14px;
		color: #6b7280;
		min-width: 60px;
		text-align: center;
		font-weight: 500;
	}

	.preview-placeholder {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
	}

	.audio-info {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
		padding: 12px;
		width: 100%;
		box-sizing: border-box;
	}

	.audio-name {
		font-size: 12px;
		color: #374151;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 100%;
	}

	.audio-player {
		width: 100%;
		width: 300px;
		height: 40px;
		padding: 4px 0;
	}

	.video-info {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		padding: 8px;
		width: 100%;
		box-sizing: border-box;
	}

	.video-player {
		width: 100%;
		max-width: 360px;
		height: auto;
		max-height: 200px;
		object-fit: contain;
		outline: none;
		cursor: pointer;
	}

	.video-info {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		width: 100%;
		pointer-events: auto;
	}

	.video-name {
		font-size: 12px;
		color: #374151;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 100%;
	}

	.video-thumb {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.video-icon {
		position: absolute;
		font-size: 32px;
		color: rgba(255, 255, 255, 0.8);
		text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
	}

	.canvas-node.text {
		border-left: 3px solid #6366f1;
	}

	.canvas-node.image {
		border-left: 3px solid #10b981;
	}

	.canvas-node.audio {
		border-left: 3px solid #f59e0b;
	}

	.canvas-node.video {
		border-left: 3px solid #ec4899;
	}

	.text-editor-panel {
		position: absolute;
		top: calc(100% + 8px);
		left: -20px;
		right: -20px;
		background-color: #f5f5f5;
		border-radius: 8px;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
		padding: 16px;
		z-index: 30;
	}

	.text-editor-panel.expanded {
		left: -100px;
		right: -100px;
	}

	.panel-arrow {
		position: absolute;
		top: -6px;
		left: 24px;
		width: 0;
		height: 0;
		border-left: 6px solid transparent;
		border-right: 6px solid transparent;
		border-bottom: 6px solid #f5f5f5;
	}

	.panel-toolbar {
		display: flex;
		justify-content: flex-end;
		gap: 16px;
		margin-bottom: 8px;
	}

	.panel-toolbar i {
		font-size: 16px;
		color: #9ca3af;
		cursor: pointer;
		transition: color 0.2s ease;
	}

	.panel-toolbar i:hover {
		color: #6366f1;
	}

	.panel-input-wrapper {
		position: relative;
		margin-bottom: 12px;
	}

	.panel-input {
		width: 100%;
		height: 100px;
		min-height: 100px;
		max-height: 300px;
		border: none;
		background-color: #fff;
		font-size: 13px;
		line-height: 1.6;
		color: #374151;
		outline: none;
		padding: 12px;
		border-radius: 4px;
		box-sizing: border-box;
		resize: vertical;
	}

	.text-editor-panel.expanded .panel-input {
		height: 200px;
		min-height: 200px;
	}

	.panel-input::placeholder {
		color: #9ca3af;
	}

	.node-suggestions {
		position: absolute;
		top: calc(100% + 4px);
		left: 0;
		right: 0;
		background-color: #fff;
		border-radius: 8px;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
		max-height: 200px;
		overflow-y: auto;
		z-index: 40;
		padding: 8px;
	}

	.suggestion-item {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 6px 10px 6px 6px;
		cursor: pointer;
		transition: all 0.2s ease;
		background-color: #d1fae5;
		border-radius: 20px;
		margin: 4px;
	}

	.suggestion-item:hover {
		background-color: #a7f3d0;
		transform: translateY(-1px);
	}

	.tag-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		background-color: #059669;
		color: #fff;
		border-radius: 50%;
		font-size: 10px;
		font-weight: bold;
	}

	.tag-name {
		font-size: 13px;
		color: #059669;
		font-weight: 500;
	}

	.tag-close {
		font-size: 12px;
		color: #059669;
		cursor: pointer;
		padding: 2px;
		transition: opacity 0.2s ease;
	}

	.tag-close:hover {
		opacity: 0.7;
	}

	.panel-footer {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 12px;
	}

	.panel-footer-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		gap: 12px;
	}

	.image-settings,
	.video-settings {
		position: relative;
	}

	.settings-summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 8px 12px;
		background-color: #fff;
		border: 1px solid #e5e7eb;
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.settings-summary:hover {
		border-color: #10b981;
		background-color: #f0fdf4;
	}

	.summary-text {
		font-size: 13px;
		color: #374151;
		font-weight: 500;
	}

	.settings-summary i {
		font-size: 14px;
		color: #9ca3af;
		transition: transform 0.2s ease;
	}

	.settings-detail {
		position: absolute;
		top: calc(100% + 8px);
		left: 0;
		background-color: #fff;
		border: 1px solid #e5e7eb;
		border-radius: 8px;
		padding: 16px;
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
		z-index: 1000;
		min-width: 200px;
	}

	.setting-group {
		margin-bottom: 8px;
	}

	.setting-label {
		font-size: 12px;
		color: #666;
		margin-bottom: 6px;
		display: block;
	}

	.aspect-grid {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	.aspect-grid.video {
		display: flex;
		gap: 8px;
	}

	.aspect-btn {
		padding: 4px 12px;
		border: 1px solid #ddd;
		border-radius: 4px;
		background: #fff;
		font-size: 12px;
		cursor: pointer;
		transition: all 0.2s;
	}

	.aspect-btn:hover {
		border-color: #10b981;
		color: #10b981;
	}

	.aspect-btn.active {
		background-color: #d1fae5;
		border-color: #10b981;
		color: #059669;
	}

	.resolution-group {
		display: flex;
		gap: 8px;
	}

	.resolution-btn {
		padding: 4px 16px;
		border: 1px solid #ddd;
		border-radius: 4px;
		background: #fff;
		font-size: 12px;
		cursor: pointer;
		transition: all 0.2s;
	}

	.resolution-btn:hover {
		border-color: #10b981;
		color: #10b981;
	}

	.resolution-btn.active {
		background-color: #d1fae5;
		border-color: #10b981;
		color: #059669;
	}

	.duration-group {
		display: flex;
		align-items: center;
		gap: 12px;
		width: 100%;
	}

	.duration-slider {
		flex: 1;
		height: 6px;
		border-radius: 3px;
		background: #ddd;
		outline: none;
		cursor: pointer;
	}

	.duration-slider::-webkit-slider-thumb {
		width: 16px;
		height: 16px;
		border-radius: 50%;
		background: #10b981;
		cursor: pointer;
		border: 2px solid #fff;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
	}

	.duration-value {
		font-size: 12px;
		color: #666;
		min-width: 30px;
	}

	.model-select {
		width: 160px;
	}

	.send-btn {
		width: 36px;
		height: 36px;
		border: none;
		border-radius: 50%;
		background-color: #10b981;
		color: #fff;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all 0.2s ease;
	}

	.send-btn:hover {
		background-color: #059669;
		transform: scale(1.05);
	}

	.send-btn i {
		font-size: 16px;
	}

	.toolbar {
		position: fixed;
		bottom: 24px;
		left: 50%;
		transform: translateX(-50%);
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 16px;
		background-color: #fff;
		border-radius: 24px;
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
	}

	.toolbar-btn {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 8px 16px;
		border: none;
		background-color: #f5f5ff;
		border-radius: 16px;
		cursor: pointer;
		font-size: 14px;
		color: #374151;
		transition: all 0.2s ease;
	}

	.toolbar-btn:hover {
		background-color: rgba(99, 102, 241, 0.15);
		color: #6366f1;
	}

	.toolbar-btn i {
		font-size: 16px;
	}

	.zoom-info {
		margin-left: 12px;
		padding-left: 12px;
		border-left: 1px solid #e0e0ff;
		font-size: 14px;
		color: #6b7280;
		font-weight: 500;
	}

	.node-upload-bar {
		position: absolute;
		top: -44px;
		left: 0;
		right: 0;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 8px 16px;
		background-color: #f5f5f5;
		border-radius: 8px 8px 0 0;
		border: 1px solid #e5e7eb;
		border-bottom: none;
		z-index: 25;
	}

	.upload-label {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 13px;
		color: #374151;
	}

	.upload-label i {
		font-size: 14px;
	}

	.upload-btn {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 6px 12px;
		background-color: #10b981;
		color: #fff;
		border-radius: 16px;
		font-size: 12px;
		cursor: pointer;
		transition: background-color 0.2s ease;
	}

	.upload-btn:hover {
		background-color: #059669;
	}

	.upload-btn i:first-child {
		font-size: 12px;
	}

	.hidden-input-wrapper {
		position: absolute;
		visibility: hidden;
		width: 0;
		height: 0;
		overflow: hidden;
	}

	.hidden-input {
		position: absolute;
		visibility: hidden;
		width: 0;
		height: 0;
		overflow: hidden;
	}

	.asset-filter-group {
		display: flex;
		gap: 8px;
		margin-bottom: 16px;
		padding-bottom: 12px;
		border-bottom: 1px solid #e0e0e0;
	}

	.filter-btn {
		padding: 6px 16px;
		border: 1px solid #d9d9d9;
		border-radius: 20px;
		background-color: #fff;
		color: #666;
		font-size: 13px;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.filter-btn:hover {
		border-color: #10b981;
		color: #10b981;
	}

	.filter-btn.active {
		background-color: #10b981;
		border-color: #10b981;
		color: #fff;
	}

	.asset-library-list {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		max-height: 400px;
		overflow-y: auto;
		padding: 12px 0;
	}

	.asset-item {
		width: calc(25% - 10px);
		cursor: pointer;
		text-align: center;
	}

	.asset-preview {
		width: 100%;
		height: 80px;
		border-radius: 8px;
		overflow: hidden;
		background-color: #f5f5f5;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.asset-preview img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.audio-preview-item {
		background-color: #fef3c7;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 8px;
	}

	.video-preview-item {
		background-color: #000;
	}

	.video-preview-item video {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.audio-preview-item i {
		font-size: 24px;
		color: #f59e0b;
	}

	.asset-audio-player {
		width: 100%;
		max-width: 200px;
		height: 36px;
	}

	.asset-name {
		display: block;
		font-size: 12px;
		color: #374151;
		margin-top: 4px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 100%;
	}

	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 40px;
		color: #9ca3af;
	}

	.empty-state i {
		font-size: 48px;
		margin-bottom: 12px;
	}

	.empty-state span {
		font-size: 14px;
	}
</style>